<template>
  <nav class="navbar">
    <div class="lighter-banner">
      <!-- Mobile header with logo and menu button -->
      <div class="mobile-header">
        <div class="mobile-logo">
          <router-link to="/">
            <img src="/images/logo.png" alt="Real Yunnan Travel" />
          </router-link>
        </div>
        <button class="mobile-menu-button" @click="toggleMobileMenu">
          <font-awesome-icon :icon="isMobileMenuOpen ? 'times' : 'bars'" />
        </button>
      </div>
    </div>
    <div class="container navbar-container">
      <div class="nav-content">
        <div class="logo desktop-logo">
          <router-link to="/">
            <img src="/images/logo.png" alt="Real Yunnan Travel" />
          </router-link>
        </div>
        
        <!-- Add backdrop for mobile menu -->
        <div class="mobile-menu-backdrop" v-if="isMobileMenuOpen" @click="toggleMobileMenu"></div>
        
        <!-- Side drawer navigation for mobile -->
        <div class="nav-links side-drawer" :class="{ 'open': isMobileMenuOpen }">
          <!-- Mobile Navigation -->
          <template v-if="isMobile">
            <router-link to="/" class="nav-link first-nav-item" @click="closeMobileMenu">Home</router-link>
            <router-link to="/destination/" class="nav-link" @click="closeMobileMenu">Destination</router-link>
            <router-link to="/tours/" class="nav-link" @click="closeMobileMenu">Yunnan Tours</router-link>
            <router-link to="/about" class="nav-link" @click="closeMobileMenu">About Us</router-link>
            <router-link to="/contact" class="nav-link" @click="closeMobileMenu">Contact Us</router-link>
            <div class="contact-email">
              <font-awesome-icon icon="envelope" /> info@realyunnan.com
            </div>
          </template>

          <!-- Desktop Navigation -->
          <template v-else>
            <router-link to="/" class="nav-link first-nav-item">Home</router-link>

            <div class="dropdown" :class="{ 'open': isDropdownOpen('destination') }" @click="toggleDropdown('destination')">
              <router-link :to="`/destination/`" class="nav-link dropdown-toggle">
                Destination
                <font-awesome-icon icon="chevron-down" class="dropdown-icon" />
              </router-link>
              <div class="dropdown-content">
                <router-link 
                  v-for="city in destinationCategories" 
                  :key="city"
                  :to="`/destination/${city}`"
                  class="dropdown-item"
                >
                  {{ cityNames[city] }}
                </router-link>
              </div>
            </div>

            <div class="dropdown" :class="{ 'open': isDropdownOpen('tours') }" @click="toggleDropdown('tours')">
              <router-link :to="`/tours/`" class="nav-link dropdown-toggle">
                Yunnan Tours
                <font-awesome-icon icon="chevron-down" class="dropdown-icon" />
              </router-link>
              <ul class="dropdown-content">
                <li v-for="(category, key) in tourCategories" :key="key">
                  <router-link :to="`/tours?category=${key}`" class="dropdown-item">
                    {{ category.name }}
                  </router-link>
                </li>
              </ul>
            </div>

            <router-link to="/about" class="nav-link">About Us</router-link>
            <router-link to="/contact" class="nav-link">Contact Us</router-link>

            <div class="contact-email">
              <font-awesome-icon icon="envelope" /> info@realyunnan.com
            </div>
          </template>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const route = useRoute();
const router = useRouter();
const isDark = ref(false);
const isMobileMenuOpen = ref(false);
const openDropdowns = ref([]);
const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

const closeDropdowns = () => {
  openDropdowns.value = [];
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const toggleDropdown = (name) => {
  if (openDropdowns.value.includes(name)) {
    openDropdowns.value = openDropdowns.value.filter(item => item !== name);
  } else {
    openDropdowns.value = [name];
  }
};

const isDropdownOpen = (name) => {
  return openDropdowns.value.includes(name);
};

const destinationCategories = [
  'kunming',
  'lijiang',
  'dali',
  'yuanyang',
  'shangri-la',
  'puer',
  'jianshui',
  'deqin'
];

const cityNames = {
  'kunming': 'Kunming',
  'lijiang': 'Lijiang',
  'dali': 'Dali',
  'yuanyang': 'Yuanyang',
  'shangri-la': 'Shangri-La',
  'puer': 'Pu\'er',
  'jianshui': 'Jianshui',
  'deqin': 'Deqin'
};

const tourCategories = {
  'ClassicTour': {
    name: "Classic Tours"
  },
  'TeaTour': {
    name: "Tea Culture Tours"
  },
  'TrekkingTour': {
    name: "Trekking Tours"
  },
  'ShortDayTour': {
    name: "Short Day Tours"
  }
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
  
  const savedTheme = localStorage.getItem('theme');
  
  // Only use saved theme or default to light mode, ignoring system preference
  isDark.value = savedTheme === 'dark';
  applyTheme(isDark.value);
  
  // Set theme in localStorage if not already set
  if (!savedTheme) {
    localStorage.setItem('theme', 'light');
  }
});

const toggleTheme = () => {
  isDark.value = !isDark.value;
  applyTheme(isDark.value);
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light');
};

const applyTheme = (dark) => {
  document.documentElement.classList.remove('light-theme', 'dark-theme');
  document.documentElement.classList.add(dark ? 'dark-theme' : 'light-theme');
};
</script>

<style scoped>
.navbar {
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
}

.lighter-banner {
  height: 10px;
  background-color: #435360;
  width: 100%;
}

/* Mobile header */
.mobile-header {
  display: none;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

.mobile-logo {
  display: none;
}

.mobile-menu-button {
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
}

.navbar-container {
  background-color: #1b2d3b;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  height: 90px;
  margin: 0 auto;
}

.nav-content {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 0 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.logo a {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: white;
}

.logo img {
  height: 80px;
  margin-right: 10px;
  transition: transform 0.3s ease;
}

.logo a:hover img {
  transform: scale(1.05);
}

.logo a:hover {
  background-color: transparent;
}

.logo span {
  font-size: 1.5rem;
  font-weight: bold;
  /* color: white; */
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;
  height: 100%;
}

.nav-link {
  text-decoration: underline;
  color: #9ca3af;
  font-weight: 700;
  font-style: italic;
  padding: 0.5rem 1rem;
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  border-radius: 4px;
  overflow: hidden;
}

.nav-link:hover,
.nav-link.active {
  color: #ffffff;
  background-color: rgba(67, 83, 96, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.nav-link::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #30798b;
  transform: scaleX(0);
  transform-origin: right;
  transition: transform 0.4s ease;
}

.nav-link:hover::before,
.nav-link.active::before {
  transform: scaleX(1);
  transform-origin: left;
}

.dropdown {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
}

.dropdown-content {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  background-color: #1b2d3b;
  min-width: 220px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  border-radius: 6px;
  padding: 0.75rem 0;
  transform: translateY(10px);
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}

.dropdown-icon {
  padding-left: 0.2rem;
}

.dropdown:hover .dropdown-content {
  display: block;
  transform: translateY(0);
  opacity: 1;
  animation: fadeIn 0.3s ease forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-content a {
  color: #9ca3af;
  font-weight: 700;
  font-style: italic;
  text-decoration: underline;
  padding: 0.75rem 1.5rem;
  display: block;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.dropdown-content a::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 3px;
  height: 100%;
  background-color: #30798b;
  transform: scaleY(0);
  transform-origin: bottom;
  transition: transform 0.3s ease;
}

.dropdown-content a:hover,
.dropdown-content a.active {
  background-color: rgba(67, 83, 96, 0.4);
  color: #ffffff;
  padding-left: 2rem;
}

.dropdown-content a:hover::before,
.dropdown-content a.active::before {
  transform: scaleY(1);
}

.dropdown-content .lighter-banner {
  margin-top: 0.75rem;
  height: 4px;
}

.contact-email {
  color: #9ca3af;
  font-weight: 700;
  font-style: italic;
  text-decoration: underline;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  padding: 0.5rem 1rem;
  border-radius: 20px;
}

.contact-email:hover {
  color: #ffffff;
  background-color: rgba(67, 83, 96, 0.4);
  transform: translateY(-2px);
}

.theme-toggle {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.theme-toggle:hover {
  background-color: rgba(67, 83, 96, 0.4);
  color: #ffffff;
  transform: rotate(180deg);
}

.theme-toggle i {
  font-size: 1.2rem;
}

@media (max-width: 1024px) {
  .nav-content {
    justify-content: space-between;
  }
}

@media (max-width: 768px) {
  /* Refine the lighter banner and mobile header */
  .lighter-banner {
    height: 60px;
    padding: 0;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.15);
    position: relative;
    z-index: 1002;
    background-color: #1b2d3b;
  }

  .mobile-header {
    display: flex;
    padding: 0 1.25rem;
    height: 100%;
  }

  .mobile-logo {
    display: block;
  }

  .mobile-logo img {
    height: 40px;
    margin-right: 0;
    transition: all 0.2s ease;
  }

  /* Stylish hamburger menu button */
  .mobile-menu-button {
    display: flex;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.1);
    transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
  }

  .mobile-menu-button:hover, 
  .mobile-menu-button:focus {
    background-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }

  .navbar-container {
    height: auto;
    padding: 0;
  }
  
  .nav-content {
    flex-direction: column;
    padding: 0;
  }

  .desktop-logo {
    display: none;
  }

  /* Add backdrop for side drawer */
  .mobile-menu-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    animation: fadeIn 0.3s ease;
  }

  /* Side drawer styling */
  .nav-links.side-drawer {
    position: fixed;
    top: 0;
    right: -300px;
    width: 300px;
    height: 100vh;
    background-color: #1b2d3b;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.2);
    transition: right 0.3s ease;
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
    z-index: 1001;
    max-height: 50vh;
    overflow-y: auto;
    align-items: flex-start;
    padding-top: 60px;
  }

  .nav-links.side-drawer.open {
    right: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 70px;
    padding-bottom: 15px;
  }

  .nav-link {
    margin: 0;
    padding: 0.3rem 1.2rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    font-weight: 500;
    text-decoration: none;
    color: #fff;
    background: none;
    font-size: 1rem;
    font-style: normal;
    position: relative;
    overflow: visible;
    transition: all 0.2s;
    width: 100%;
    justify-content: flex-start;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .contact-email {
    margin-top: 10px;
    padding: 0.7rem;
    text-align: center;
    color: #fff;
    background-color: rgba(48, 121, 139, 0.12);
    border-top: 1px solid rgba(255,255,255,0.05);
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border-radius: 8px;
    margin: 10px 1.2rem;
    width: calc(100% - 2.4rem);
  }

  .contact-email svg {
    font-size: 1.1rem;
  }
}
</style> 